/**
 * TUVI - Resumen General del Proyecto
 * Dashboard con estadísticas y progreso general
 */

import React, { useState, useEffect } from 'react';
import client from '../../../services/graphqlClient';
import './ResumenProyecto.css';

const ResumenProyecto = ({ proyecto, onVerPosventa }) => {
  const [cajaMenorTotal, setCajaMenorTotal] = useState(0);
  const [cronograma, setCronograma] = useState(null);

  useEffect(() => {
    if (proyecto?.proyecto?.id) {
      cargarCajaMenor();
      cargarCronograma();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proyecto?.proyecto?.id]);

  const cargarCronograma = async () => {
    try {
      const data = await client.query(`
        query($proyecto_id: Int!) {
          cronogramaProyecto(proyecto_id: $proyecto_id) {
            estadisticas {
              fecha_inicio fecha_fin total_actividades
              actividades_completadas actividades_en_progreso actividades_pendientes
              dias_totales progreso_porcentaje
            }
            cronograma { id es_hito fecha_fin fecha_fin_real fecha_inicio_real duracion_dias estado_calculado }
          }
        }
      `, { proyecto_id: proyecto.proyecto.id });

      const stats = data.cronogramaProyecto.estadisticas;
      if (stats && stats.total_actividades > 0) {
        // Dias de retraso = el MAXIMO retraso de cualquier actividad del cronograma.
        // Por actividad:
        //   - Si esta completada (fecha_fin_real): max(0, fecha_fin_real - fecha_fin_planeada)
        //   - Si NO completada y fecha_fin ya paso: max(0, hoy - fecha_fin)
        //   - En otro caso: 0
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        let maxRetraso = 0;
        for (const item of (data.cronogramaProyecto.cronograma || [])) {
          if (!item.fecha_fin) continue;
          const ffPlan = new Date(item.fecha_fin + 'T00:00:00');
          let retrasoItem = 0;
          if (item.fecha_fin_real) {
            const ffReal = new Date(item.fecha_fin_real + 'T00:00:00');
            retrasoItem = Math.max(0, Math.floor((ffReal - ffPlan) / 86400000));
          } else if (ffPlan < hoy) {
            retrasoItem = Math.floor((hoy - ffPlan) / 86400000);
          }
          if (retrasoItem > maxRetraso) maxRetraso = retrasoItem;
        }

        setCronograma({
          fecha_inicio: stats.fecha_inicio,
          fecha_fin: stats.fecha_fin,
          total_actividades: stats.total_actividades,
          completadas: stats.actividades_completadas,
          en_progreso: stats.actividades_en_progreso,
          pendientes: stats.actividades_pendientes,
          dias_totales: stats.dias_totales,
          progreso: stats.progreso_porcentaje,
          dias_retraso: maxRetraso,
        });
      }
    } catch (err) {}
  };

  const cargarCajaMenor = async () => {
    try {
      // Caja menor total del proyecto viene en estadísticas del detalle
      const total = proyecto?.estadisticas?.total_caja_menor || 0;
      setCajaMenorTotal(parseFloat(total));
    } catch (error) {
      console.error('Error al cargar caja menor:', error);
    }
  };

  if (!proyecto) return null;

  const { proyecto: info, estadisticas, capitulos } = proyecto;

  // Formatear moneda
  const formatMoney = (valor) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(valor || 0);
  };

  // Formatear fecha
  const formatDate = (fecha) => {
    if (!fecha) return 'No definida';
    const date = new Date(fecha + 'T00:00:00');
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Monto pagado por el cliente (viene de cartera)
  const montoPagado = parseFloat(info.monto_pagado || 0);
  const montoPendiente = parseFloat(info.monto_pendiente || info.valor_final - montoPagado);

  // Calcular suma total de gastos por capítulo (valor_ejecutado_total es la suma de gastos reales)
  const ejecutadoFacturasCalc = capitulos.reduce((sum, cap) => {
    return sum + parseFloat(cap.valor_ejecutado_total || 0);
  }, 0);
  // gasto_manual es una BASE (gasto ya realizado por fuera de TUVI). Las facturas
  // que se registren en TUVI se SUMAN encima de esa base.
  const gastoManualBase = (info.gasto_manual != null && info.gasto_manual !== '') ? parseFloat(info.gasto_manual) : 0;
  // Facturas del proyecto que no tienen capítulo: no entran en ningún capítulo,
  // pero son gasto del proyecto (así las cuenta la Vista Financiera). Se suman
  // aquí y se muestran aparte para que se vea lo que falta por clasificar.
  const facturasSinCapitulo = parseFloat(estadisticas?.total_facturas_sin_capitulo || 0);
  const cantidadSinCapitulo = parseInt(estadisticas?.cantidad_facturas_sin_capitulo || 0, 10);
  const totalGastadoFacturas = ejecutadoFacturasCalc + facturasSinCapitulo + gastoManualBase;

  // Total de salidas de bodega del proyecto
  const totalSalidasBodega = parseFloat(estadisticas?.total_salidas_bodega || 0);

  // Gasto de POSVENTA (garantías) del apartamento — va aparte del presupuesto de obra
  const totalPosventa = parseFloat(estadisticas?.total_posventa || 0);
  const cantidadPosventa = parseInt(estadisticas?.cantidad_posventa || 0, 10);

  // Ajustes de cartera: reintegros a cliente + descuentos por incumplimiento.
  // Ambos reducen el valor total del proyecto (modelo A). El reintegro además
  // reduce el recaudado neto (ya viene descontado en info.monto_pagado).
  const totalReintegros = parseFloat(estadisticas?.total_reintegros || 0);
  const totalDescuentosCartera = parseFloat(estadisticas?.total_descuentos_cartera || 0);
  const hayAjustes = totalReintegros > 0 || totalDescuentosCartera > 0;
  const valorAjustado = (info.valor_ajustado != null)
    ? parseFloat(info.valor_ajustado)
    : (parseFloat(info.valor_final || 0) - totalReintegros - totalDescuentosCartera);

  // Control de Presupuesto con las cifras de la Vista Financiera para este
  // proyecto (mismo cálculo del API: facturas pagadas prorrateadas + gasto manual
  // + caja menor + bodega), para que el total sea exactamente el de gerencia.
  // Si el API no las trae (versión vieja), se usa el cálculo por capítulos.
  const tieneVF = estadisticas?.vf_total_ejecutado != null;
  const facturasPagadas = tieneVF ? parseFloat(estadisticas.vf_facturas_pagadas || 0) : ejecutadoFacturasCalc + facturasSinCapitulo;
  const facturasMontadas = tieneVF ? parseFloat(estadisticas.vf_facturas_montadas || 0) : ejecutadoFacturasCalc + facturasSinCapitulo;
  const gastoManualVF = tieneVF ? parseFloat(estadisticas.vf_gasto_manual || 0) : gastoManualBase;
  const cajaMenorVF = tieneVF ? parseFloat(estadisticas.vf_caja_menor || 0) : cajaMenorTotal;
  const bodegaVF = tieneVF ? parseFloat(estadisticas.vf_salidas_bodega || 0) : totalSalidasBodega;
  const totalGastado = tieneVF
    ? parseFloat(estadisticas.vf_total_ejecutado)
    : totalGastadoFacturas + cajaMenorTotal + totalSalidasBodega;

  // Calcular presupuesto límite al 75%
  const presupuestoLimite = info.valor_final * 0.75;
  const presupuestoDisponible = presupuestoLimite - totalGastado;
  const porcentajeGastado = (totalGastado / presupuestoLimite) * 100;
  const estaEnLimite = porcentajeGastado > 75;
  const estaCerca = porcentajeGastado > 65 && porcentajeGastado <= 75;

  return (
    <div className="resumen-proyecto">
      {/* Control de Presupuesto al 75% - DESTACADO */}
      <div className={`presupuesto-control ${estaEnLimite ? 'alerta' : estaCerca ? 'advertencia' : 'normal'}`}>
        <div className="control-header">
          <h2>
            {estaEnLimite && '🚨 '}
            {estaCerca && '⚠️ '}
            {!estaEnLimite && !estaCerca && '✅ '}
            Control de Presupuesto (Límite 75%)
          </h2>
          {estaEnLimite && <span className="badge-alerta">¡LÍMITE SUPERADO!</span>}
          {estaCerca && <span className="badge-advertencia">Cerca del límite</span>}
        </div>

        <div className="control-grid">
          <div className="control-item">
            <span className="control-label">Presupuesto Total Proyecto</span>
            <span className="control-value total">{formatMoney(info.valor_final)}</span>
          </div>
          <div className="control-item">
            <span className="control-label">Límite Permitido (75%)</span>
            <span className="control-value limite">{formatMoney(presupuestoLimite)}</span>
          </div>
          <div className="control-item" title="Lo realmente pagado de las facturas del proyecto (prorrateado cuando una factura se reparte entre proyectos). Igual que en la Vista Financiera.">
            <span className="control-label">Facturas Pagadas</span>
            <span className="control-value gastado">{formatMoney(facturasPagadas)}</span>
            {facturasMontadas > facturasPagadas + 1 && (
              <span className="control-hint">Montadas: {formatMoney(facturasMontadas)} · por pagar: {formatMoney(facturasMontadas - facturasPagadas)}</span>
            )}
          </div>
          {gastoManualVF > 0 && (
            <div className="control-item" title="Gasto registrado por fuera de TUVI (base del proyecto).">
              <span className="control-label">Gasto Manual</span>
              <span className="control-value gastado">{formatMoney(gastoManualVF)}</span>
            </div>
          )}
          {cantidadSinCapitulo > 0 && (
            <div className="control-item sin-capitulo" title="Facturas asignadas al proyecto que no tienen capítulo. Ya están sumadas en Ejecutado; asígnales capítulo desde Facturas para que cuenten en el suyo.">
              <span className="control-label">Facturas sin capítulo ({cantidadSinCapitulo})</span>
              <span className="control-value gastado">{formatMoney(facturasSinCapitulo)}</span>
              <span className="control-hint">Ya incluidas en Facturas Pagadas. Asígnales capítulo en Facturas.</span>
            </div>
          )}
          <div className="control-item caja-menor">
            <span className="control-label">Total Caja Menor</span>
            <span className="control-value caja-menor">{formatMoney(cajaMenorVF)}</span>
          </div>
          <div className="control-item salidas-bodega">
            <span className="control-label">Salidas Bodega</span>
            <span className="control-value salidas-bodega">{formatMoney(bodegaVF)}</span>
          </div>
          <div className="control-item">
            <span className="control-label">Total Gastado</span>
            <span className="control-value gastado">{formatMoney(totalGastado)}</span>
          </div>
          <div className="control-item">
            <span className="control-label">Presupuesto Disponible</span>
            <span className={`control-value disponible ${presupuestoDisponible < 0 ? 'negativo' : 'positivo'}`}>
              {formatMoney(presupuestoDisponible)}
            </span>
          </div>
        </div>

        <div className="control-progress">
          <div className="progress-header">
            <span>Porcentaje Gastado del Límite</span>
            <span className={`progress-percentage ${estaEnLimite ? 'peligro' : estaCerca ? 'advertencia' : 'normal'}`}>
              {porcentajeGastado.toFixed(2)}%
            </span>
          </div>
          <div className="progress-bar-large">
            <div
              className={`progress-fill ${estaEnLimite ? 'peligro' : estaCerca ? 'advertencia' : 'normal'}`}
              style={{ width: `${Math.min(porcentajeGastado, 100)}%` }}
            ></div>
            <div className="progress-marker" style={{ left: '75%' }}>
              <span className="marker-label">75%</span>
            </div>
          </div>
        </div>

        {estaEnLimite && (
          <div className="control-mensaje alerta">
            🚨 <strong>ALERTA:</strong> El gasto ha superado el 75% del presupuesto total. Se requiere autorización para continuar.
          </div>
        )}
        {estaCerca && (
          <div className="control-mensaje advertencia">
            ⚠️ <strong>ADVERTENCIA:</strong> El gasto está cerca del límite del 75%. Controle los gastos adicionales.
          </div>
        )}
      </div>

      {/* Sección de Estadísticas Principales */}
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <span className="stat-label">Valor Total del Proyecto</span>
            <span className="stat-value">{formatMoney(info.valor_final)}</span>
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <span className="stat-label">Valor Ejecutado</span>
            <span className="stat-value">{formatMoney(totalGastado)}</span>
            <span className="stat-subtext">
              {info.valor_final > 0 ? ((totalGastado / info.valor_final) * 100).toFixed(1) : '0.0'}% del total
            </span>
          </div>
        </div>

        <div className="stat-card info">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <span className="stat-label">Progreso General</span>
            <span className="stat-value">{cronograma?.progreso ?? 0}%</span>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${cronograma?.progreso ?? 0}%` }}
              ></div>
            </div>
            <span className="stat-subtext">
              {cronograma
                ? `${cronograma.completadas}/${cronograma.total_actividades} actividades`
                : 'Sin cronograma'}
            </span>
          </div>
        </div>

        <div className="stat-card warning">
          <div className="stat-icon">📅</div>
          <div className="stat-content">
            <span className="stat-label">Días de Retraso</span>
            <span className="stat-value">{cronograma?.dias_retraso ?? 0}</span>
            <span className="stat-subtext">
              {cronograma
                ? (cronograma.dias_retraso > 0 ? 'Requiere atención' : 'En tiempo')
                : 'Sin cronograma'}
            </span>
          </div>
        </div>

        {/* Tarjeta Posventa: solo si el apartamento tiene gasto de garantías */}
        {totalPosventa > 0 && (
          <div
            className="stat-card posventa clickable"
            onClick={() => onVerPosventa && onVerPosventa(info.id, info.proyecto_nombre, info.apartamento)}
            title="Ver las facturas de posventa de este apartamento"
          >
            <div className="stat-icon">🛠️</div>
            <div className="stat-content">
              <span className="stat-label">Posventa (Garantías)</span>
              <span className="stat-value">{formatMoney(totalPosventa)}</span>
              <span className="stat-subtext">
                {cantidadPosventa} factura{cantidadPosventa === 1 ? '' : 's'} · ver detalle →
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Información del Cliente y Proyecto */}
      <div className="info-sections">
        <div className="info-card">
          <h3>👤 Información del Cliente</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Nombre:</span>
              <span className="info-value">{info.nombre_cliente}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Cédula:</span>
              <span className="info-value">{info.cedula}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Dirección:</span>
              <span className="info-value">{info.direccion || 'No especificada'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Teléfono:</span>
              <span className="info-value">{info.telefono || 'No especificado'}</span>
            </div>
          </div>
        </div>

        <div className="info-card">
          <h3>🏗️ Detalles del Proyecto</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Apartamento:</span>
              <span className="info-value">{info.apartamento || 'No especificado'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Área:</span>
              <span className="info-value">{info.area_m2} m²</span>
            </div>
            <div className="info-item">
              <span className="info-label">Habitaciones:</span>
              <span className="info-value">{info.habitaciones}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Baños:</span>
              <span className="info-value">{info.banos}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Negocio:</span>
              <span className="info-value">{info.negocio || 'No especificado'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Ubicación:</span>
              <span className="info-value">{info.ubicacion_detallada || 'No especificada'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Equipo de Trabajo */}
      <div className="info-card">
        <h3>👥 Equipo de Trabajo</h3>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Comercial:</span>
            <span className="info-value">{info.comercial || 'No asignado'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Residente:</span>
            <span className="info-value">{info.residente || 'No asignado'}</span>
          </div>
        </div>
      </div>

      {/* Cronograma sincronizado */}
      <div className="info-card">
        <h3>📅 Cronograma y Fechas</h3>
        {cronograma ? (
          <>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Inicio Cronograma:</span>
                <span className="info-value">{formatDate(cronograma.fecha_inicio)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Entrega Programada:</span>
                <span className="info-value">{formatDate(cronograma.fecha_fin)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Días totales:</span>
                <span className="info-value">{cronograma.dias_totales} días hábiles</span>
              </div>
              <div className="info-item">
                <span className="info-label">Progreso:</span>
                <span className="info-value">{cronograma.progreso}%</span>
              </div>
            </div>
            <div className="cronograma-stats-mini">
              <span className="crono-mini-item crono-completado">{cronograma.completadas} completadas</span>
              <span className="crono-mini-item crono-progreso">{cronograma.en_progreso} en progreso</span>
              <span className="crono-mini-item crono-pendiente">{cronograma.pendientes} pendientes</span>
            </div>
          </>
        ) : (
          <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>Sin cronograma generado</p>
        )}
      </div>

      {/* Estado de Capítulos */}
      <div className="info-card">
        <h3>📑 Estado de Capítulos</h3>
        <div className="capitulos-stats">
          <div className="capitulo-stat completado">
            <span className="stat-number">{estadisticas.capitulos_completados}</span>
            <span className="stat-text">Completados</span>
          </div>
          <div className="capitulo-stat en-progreso">
            <span className="stat-number">{estadisticas.capitulos_en_progreso}</span>
            <span className="stat-text">En Progreso</span>
          </div>
          <div className="capitulo-stat retrasado">
            <span className="stat-number">{estadisticas.capitulos_retrasados}</span>
            <span className="stat-text">Retrasados</span>
          </div>
          <div className="capitulo-stat pendiente">
            <span className="stat-number">
              {estadisticas.total_capitulos - estadisticas.capitulos_completados - estadisticas.capitulos_en_progreso - estadisticas.capitulos_retrasados}
            </span>
            <span className="stat-text">Pendientes</span>
          </div>
        </div>

        <div className="capitulos-progress">
          <div className="progress-label">
            <span>Progreso de Capítulos</span>
            <span>{estadisticas.progreso_porcentaje}%</span>
          </div>
          <div className="progress-bar large">
            <div
              className="progress-fill"
              style={{ width: `${estadisticas.progreso_porcentaje}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Información Financiera */}
      <div className="info-card">
        <h3>💳 Información Financiera</h3>
        <div className="financial-grid">
          <div className="financial-item">
            <span className="financial-label">Subtotal:</span>
            <span className="financial-value">{formatMoney(info.subtotal)}</span>
          </div>
          <div className="financial-item">
            <span className="financial-label">AIU (15%):</span>
            <span className="financial-value">
              {formatMoney(info.aiu)}
            </span>
          </div>
          <div className="financial-item">
            <span className="financial-label">IVA (19%):</span>
            <span className="financial-value">
              {formatMoney(info.iva)}
            </span>
          </div>
          <div className="financial-item total">
            <span className="financial-label">Valor Final:</span>
            <span className="financial-value">{formatMoney(info.valor_final)}</span>
          </div>
        </div>

        {hayAjustes && (
          <div className="financial-grid" style={{ marginTop: 8, borderTop: '1px dashed #e2e8f0', paddingTop: 8 }}>
            {totalReintegros > 0 && (
              <div className="financial-item">
                <span className="financial-label">↩️ Reintegros a cliente:</span>
                <span className="financial-value" style={{ color: '#c2410c' }}>− {formatMoney(totalReintegros)}</span>
              </div>
            )}
            {totalDescuentosCartera > 0 && (
              <div className="financial-item">
                <span className="financial-label">🏷️ Descuentos incumplimiento:</span>
                <span className="financial-value" style={{ color: '#b45309' }}>− {formatMoney(totalDescuentosCartera)}</span>
              </div>
            )}
            <div className="financial-item total">
              <span className="financial-label">Valor Total Ajustado:</span>
              <span className="financial-value" style={{ color: '#15803d' }}>{formatMoney(valorAjustado)}</span>
            </div>
          </div>
        )}

        <div className="payment-summary">
          <div className="payment-item paid">
            <span className="payment-label">💰 Monto Pagado:</span>
            <span className="payment-value">{formatMoney(montoPagado)}</span>
          </div>
          <div className="payment-item pending">
            <span className="payment-label">⏳ Monto Pendiente:</span>
            <span className="payment-value">{formatMoney(montoPendiente)}</span>
          </div>
        </div>
      </div>

      {/* Pagos Adicionales */}
      {(info.pago_inicial > 0 || info.anticipo_carpinteria > 0 || info.deposito_admon > 0) && (
        <div className="info-card">
          <h3>💵 Pagos Especiales</h3>
          <div className="info-grid">
            {info.pago_inicial > 0 && (
              <div className="info-item">
                <span className="info-label">Pago Inicial:</span>
                <span className="info-value">
                  {formatMoney(info.pago_inicial)}
                  {info.fecha_pago_inicial && ` - ${formatDate(info.fecha_pago_inicial)}`}
                </span>
              </div>
            )}
            {info.anticipo_carpinteria > 0 && (
              <div className="info-item">
                <span className="info-label">Anticipo Carpintería:</span>
                <span className="info-value">
                  {formatMoney(info.anticipo_carpinteria)}
                  {info.fecha_anticipo_carpinteria && ` - ${formatDate(info.fecha_anticipo_carpinteria)}`}
                </span>
              </div>
            )}
            {info.deposito_admon > 0 && (
              <div className="info-item">
                <span className="info-label">Depósito Administración:</span>
                <span className="info-value">
                  {formatMoney(info.deposito_admon)}
                  {info.fecha_deposito_admon && ` - ${formatDate(info.fecha_deposito_admon)}`}
                </span>
              </div>
            )}
            {info.llaves_entregadas && (
              <div className="info-item">
                <span className="info-label">🔑 Llaves Entregadas:</span>
                <span className="info-value">
                  Sí {info.fecha_entrega_llaves && ` - ${formatDate(info.fecha_entrega_llaves)}`}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notas y Observaciones */}
      {(info.notas_generales || info.motivo_retrasos) && (
        <div className="info-card">
          <h3>📝 Notas y Observaciones</h3>
          {info.notas_generales && (
            <div className="nota-section">
              <h4>Notas Generales:</h4>
              <p>{info.notas_generales}</p>
            </div>
          )}
          {info.motivo_retrasos && (
            <div className="nota-section alert">
              <h4>⚠️ Motivo de Retrasos:</h4>
              <p>{info.motivo_retrasos}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ResumenProyecto;
